import { queryLog } from "@/db/audit";
import { getBuildInfo } from "@/lib/buildInfo";
import { getRecentErrors, type CapturedError } from "@/lib/consoleCapture";
import { getStorageInventory } from "@/lib/privacy/inventory";
import { nowIso } from "@/lib/uid";

/**
 * Build the diagnostic payload for a manual bug report. The user always reviews
 * the full JSON and must explicitly consent before it is sent (see
 * BugReportDialog). This function only *gathers* local data; it sends nothing.
 */

export interface BugReportUserFields {
  trying: string;
  happened: string;
  expected: string;
}

export interface BugReport {
  generated_at: string;
  app: { version: string; commit: string; build_url: string | null };
  browser: { user_agent: string; language: string; platform: string; viewport: string };
  url: string;
  user: BugReportUserFields;
  recent_audit: { timestamp: string; category: string; action: string }[];
  console_errors: CapturedError[];
  storage_summary: { category: string; count: number; bytes: number }[];
}

export const APP_VERSION = "0.3.0-dev";

export async function buildBugReport(user: BugReportUserFields): Promise<BugReport> {
  const build = getBuildInfo();
  const [audit, storage] = await Promise.all([queryLog({ limit: 20 }), getStorageInventory()]);

  return {
    generated_at: nowIso(),
    app: { version: APP_VERSION, commit: build.commitShort, build_url: build.buildUrl },
    browser: {
      user_agent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      viewport: `${window.innerWidth}×${window.innerHeight}`,
    },
    url: window.location.href,
    user,
    recent_audit: audit.map((e) => ({ timestamp: e.timestamp, category: e.category, action: e.action })),
    console_errors: getRecentErrors(),
    storage_summary: storage.map((c) => ({ category: c.label, count: c.count, bytes: c.bytes })),
  };
}
